import React from 'react';
import ValidationService from '../../services/validationService';
import PropTypes from 'prop-types';
import BaseInputValidationComponent from './BaseInputValidationComponent';
import ValidationErrors from './ValidationErrors';

class TextInput extends BaseInputValidationComponent {
    constructor(props, context) {
        super(props, context);
        this.onInput = this.onInput.bind(this);
        this.handleKeyPress = this.handleKeyPress.bind(this);
        this.defaultRequiredErrorMessage = (name) => `${name} is required`;
        this.defaultMinCharsError = (name, minChars) => `${name}  must be at least ${minChars} characters long`;
        this.defaultInvalidCharsError = (name) => `${name} is invalid`;
    }

    componentDidMount() {
        this.validateString("");
    }

    handleKeyPress(event) {
        if (this.props.preventInvalidCharsOnKeypress && ValidationService.doesStringHaveInvalidCharacters(event.key)) {
            event.preventDefault();
        }
    }

    onInput(event) {
        event && event.preventDefault();
        this.props.onChange && this.props.onChange(event);
        this.validateString(event.target.value);
    }

    validateString(stringToValidate) {
        let errArray = [];
        let name = this.props.name;

        if (this.props.isRequired && ValidationService.isStringNullOrEmpty(stringToValidate)) {
            let errorMessage = !this.props.isRequired.messageOnError ?
                this.defaultRequiredErrorMessage(name) : this.props.isRequired.messageOnError(stringToValidate);

            errArray.push(errorMessage);
        }
        if (this.props.minChars) {
            let minChars = this.props.minChars.minChars ? this.props.minChars.minChars : this.props.minChars;

            if (stringToValidate.length < minChars) {
                let errorMessage = !this.props.minChars.messageOnError ?
                    this.defaultMinCharsError(name, minChars) : this.props.minChars.messageOnError(stringToValidate);

                errArray.push(errorMessage);
            }
        }
        if (this.props.checkForInvalidChars && ValidationService.doesStringHaveInvalidCharacters(stringToValidate)) {
            let errorMessage = !this.props.checkForInvalidChars.messageOnError ?
                this.defaultInvalidCharsError(name) : this.props.checkForInvalidChars.messageOnError(stringToValidate);

            errArray.push(errorMessage);
        }

        let isValid = errArray.length === 0;
        this.setState({ errorMessages: errArray, isValid: isValid });

        this.notifyOfValidation(name, isValid, errArray);
    }

    render() {
        var { name, minChars, isRequired, checkForInvalidChars, onValidate, onChange, updateFormOnValidation, errorMessageCssClass, showErrorMessages, preventInvalidCharsOnKeypress, ...rest } = this.props;

        return <React.Fragment>
            <input
                onKeyPress={this.handleKeyPress}
                onChange={this.onInput}
                ref={this.props.inputReference}
                {...rest}
            />
            {
                showErrorMessages && <ValidationErrors
                    className={errorMessageStyleClass}
                    errorMessages={this.getErrorMessage()}
                />
            }
        </React.Fragment>;
    }
}

TextInput.defaultProps = {
    isRequired: false,
    minChars: 0,
    checkForInvalidChars: false,
    showErrorMessages: false,
    errorMessageCssClass: "",
    preventInvalidCharsOnKeypress: false,
    inputReference: null
};

TextInput.propTypes = {
    ...BaseInputValidationComponent.propTypes,
    /** marks field as required for validation */
    isRequired: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.shape({
            /** callback func to override the default error message for this validation
             * @param {input} the current value of the input element
             * @returns {string} the error message for this validation
             */
            messageOnError: PropTypes.func.isRequired
        })
    ]),
    /** sets a minimum number of characters to check for during validation */
    minChars: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({
            minChars: PropTypes.number,
            /** callback func to override the default error message for this validation
             * @param {input} the current value of the input element
             * @returns {string} the error message for this validation
             */
            messageOnError: PropTypes.func
        })
    ]),
    /** sets a maximum number of characters to check for during validation */
    maxChars: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({
            minChars: PropTypes.number,
            /** callback func to override the default error message for this validation
             * @param {input} the current value of the input element
             * @returns {string} the error message for this validation
             */
            messageOnError: PropTypes.func
        })
    ]),
    /** checks for "invalid" characters as specified by regex - @see {ValidationService}
     * @example - checkForInvalidChars={2}
     * @example - checkForInvalidChars={{ minChars: 2, messageOnError: (input) => `${input} is invalid`}}
     */
    checkForInvalidChars: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.shape({
            /** callback func to override the default error message for this validation
             * @param {input} the current value of the input element
             * @returns {string} the error message for this validation
             */
            messageOnError: PropTypes.func.isRequired
        })
    ]),
    /** display validation error message onChange */
    showErrorMessages: PropTypes.bool,
    /** css class to apply to error messages */
    errorMessageCssClass: PropTypes.string,
    /** callback that triggers on input change */
    onChange: PropTypes.func,
    /** unique name to identify input component */
    name: PropTypes.string.isRequired,
    /** prevents WBM-defined invalid characters from being written to the textbox */
    preventInvalidCharsOnKeypress: PropTypes.bool,
    /** element reference for the input created using React.createRef() */
    inputReference: PropTypes.shape({ current: PropTypes.elementType })
};

export default TextInput;
